import { Big } from 'big.js';
import { match } from 'ts-pattern';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderContext } from '~/tenants/bre/model/BreOrderContext';
import { BreOrderType } from '~/tenants/bre/model/enums';
import BreFlyersConfig from '~/tenants/bre/performable/print/flyers/BreFlyersConfig';
import BreRidersConfig, { RiderShipping } from '~/tenants/bre/performable/print/riders/BreRidersConfig';
import { BreRidersPay, BreRidersRate } from '~/tenants/bre/performable/print/riders/BreRidersData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import { datetime } from '~/lib/datettime';
import { bre_print_speed } from '~/tenants/bre/performable/common';

export default class BreRidersJob extends BreJob<typeof BreRidersConfig> {
  get performable(): typeof BreRidersConfig {
    return BreRidersConfig;
  }

  get configurable(): boolean {
    return true;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Goles',
      value: this.metadata.holes,
      provider: true,
    });

    if (this.metadata.shipping) {
      info.push({
        name: 'Shipping',
        value: this.metadata.shipping,
        provider: true,
      });
    }

    info.push({
      name: 'QR Code',
      value: this.metadata.no_qr_code ? 'No' : 'Yes',
      provider: true,
    });

    info.push({
      name: 'Need By',
      value: datetime(this.metadata.need_by, this.order.location.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  get shippingType(): string {
    if (!this.metadata.shipping) {
      return 'Free Delivery';
    }

    return BreRidersJob.shippingType(this.metadata.shipping);
  }

  get shippingFee(): string {
    if (!this.metadata.shipping) {
      return '0.00';
    }

    return BreRidersJob.shippingFee(this.metadata.shipping);
  }

  static shippingType(shipping: RiderShipping): string {
    return match(shipping)
      .with('courier', () => '2-Days Courier')
      .with('ups', () => '3-Days UPS')
      .exhaustive();
  }

  static shippingFee(shipping: RiderShipping): string {
    const { COURIER, UPS } = BreRidersRate;

    return match(shipping)
      .with('courier', () => COURIER)
      .with('ups', () => UPS)
      .exhaustive();
  }

  static hasSameDayFlyers(order: BreOrderContext): boolean {
    return order.jobs.some(({ metadata, performable_id }) => {
      return performable_id === BreFlyersConfig.id && bre_print_speed(order, metadata.need_by);
    });
  }

  isDelivery(): boolean {
    return this.metadata.shipping === 'courier';
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreRidersConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      shipping: 'courier',
      holes: '2',
      need_by: datetime(this.order.created, this.order.timezone).toSQLDate(),
    };
  }

  revenueLines(): TenantJobLine[] {
    const { RIDER } = BreRidersRate;
    const { holes } = this.metadata;
    const lines: TenantJobLine[] = [];

    if (RIDER) {
      lines.push({
        id: 'riders_print',
        description: `24" x 6" Yard Sign Rider (${holes} holes)`,
        amount: new Big(RIDER),
        taxable: true,
      });
    }

    const sameFlyerDeliveryDate = this.order.jobs.some(({ metadata, performable_id }) => {
      return performable_id === BreFlyersConfig.id && metadata.need_by === this.metadata.need_by;
    });

    if (!sameFlyerDeliveryDate) {
      lines.push({
        id: 'shipping_fee',
        description: `${this.shippingType} shipping`,
        amount: new Big(this.shippingFee),
        fee: 'delivery',
      });
    }

    return lines;
  }

  expenseLines(): TenantJobLine[] {
    const { RIDER } = BreRidersPay;
    const { holes } = this.metadata;
    const lines: TenantJobLine[] = [];

    if (RIDER) {
      lines.push({
        id: 'riders_print',
        description: `24" x 6" Yard Sign Rider (${holes} holes)`,
        amount: new Big(RIDER),
      });
    }

    return lines;
  }
}
